import { useEffect } from 'react';

import useGlobalState from 'crm/GlobalStateContext';
import { StoredTableSettings, TableSettingsConfig } from 'crm/elements/common/types';
import { isPlainObject } from 'utils/helpers';

function useTableSettings(tableId: string): TableSettingsConfig {
    const { tableSettings, setTableSettings } = useGlobalState();
    const table = tableSettings[tableId] ?? { hiddenColumns: [], columnNames: [] };
    const localStorageKey = `crm_table_${tableId}_settings`;

    const saveHiddenColumns = (hiddenColumns: number[]) => {
        localStorage.setItem(localStorageKey, JSON.stringify({ hiddenColumns }));
    };

    const setHiddenColumns = (hiddenColumns: number[]) => {
        setTableSettings((settings) => ({ ...settings, [tableId]: { ...table, ...settings[tableId], hiddenColumns } }));
    };

    const setColumnNames = (columnNames: string[]) => {
        setTableSettings((settings) => ({ ...settings, [tableId]: { ...table, ...settings[tableId], columnNames } }));
    };

    const setColumnVisibility = (columnIndex: number, isVisible: boolean) => {
        if (isVisible) {
            const columns = table.hiddenColumns.filter((index) => index !== columnIndex);
            if (columns.length !== table.hiddenColumns.length) {
                setHiddenColumns(columns);
                saveHiddenColumns(columns);
            }
        } else if (!table.hiddenColumns.includes(columnIndex)) {
            const columns = [...table.hiddenColumns, columnIndex];
            setHiddenColumns(columns);
            saveHiddenColumns(columns);
        }
    };

    useEffect(() => {
        setHiddenColumns(loadTableSettings(localStorageKey).hiddenColumns);
    }, [localStorageKey]);

    return {
        hiddenColumns: table.hiddenColumns,
        setColumnVisibility,
        columnNames: table.columnNames,
        setColumnNames,
    };
}

function loadTableSettings(key: string): StoredTableSettings {
    const initialSettings: StoredTableSettings = { hiddenColumns: [] };
    try {
        const settings = JSON.parse(localStorage.getItem(key));
        if (!isPlainObject(settings)) {
            throw new Error();
        }
        return { ...initialSettings, ...settings };
    } catch {
        return initialSettings;
    }
}

export default useTableSettings;
